import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Routes from '../../constants/routes';
import useChangeFavorite from '../../hooks/use-change-favorite';
import translation from '../../languages';
import { reset } from '../../reducers/cart';
import { aiStripeEvents } from '../../services/quality-events/ai-stripe-events';
import { addToCart as addToCartApi } from '../../api/cart/add-to-cart';
import { removeFromCart } from '../../api/cart/remove-from-cart';
import qualityEvents from '../../services/quality-events/quality-events';
import { CART_REMOVE_FROM_CART_SOURCE } from '../../services/quality-events/quality-events-pages/cart-page-quality-events.constants';
import enhancedEcommerce from '../../utils/enhanced-ecommerce';
import isInFavorites from '../../utils/material/is-in-favorites';
import isMaterialInCart from '../../utils/material/is-material-in-cart';
import pubsub from '../../utils/pubsub';
import schoolAccount from '../../utils/school-account';
import session from '../../utils/session';
import { ADD_TO_CART_MODAL_TYPES } from '../add-to-cart-modal/add-to-cart-modal.constants';
import Button, { ButtonProps } from '../../ui/button/button';
import Icon from '../icon/icon';
import Spinner from '../../ui/spinner/spinner';
import { AddToCartButtonType, CartIconName } from './add-to-cart-button.types';
import './add-to-cart-button.scss';

export type AddToCartButtonProps = TranslationProps & {
    material: Material;
    alreadyInCartText?: string;
    className?: string;
    text?: string;
    title?: string;
    type?: AddToCartButtonType;
    cartIconName?: CartIconName;
    onAddToCartCallback?: (material: Material) => boolean;
    materialIndex?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    qualityEventsExtra?: any;
    onAddToCartSuccess?: (props: { cart: Cart; idsOfMaterialsIncludedInBundle: number[] }) => void;
    onAddToCartError?: VoidFunction;
    isDisabled?: boolean;
    shouldPreventCartOpen?: boolean;
    addToCartData?: { material: Array<{ id: number; purchaseType: number }> };
    scrollToTopOnSuccess?: boolean;
    hideRestrictionsMessage?: boolean;
    areAllMaterialsInCart?: boolean;
    size?: ButtonProps['size'];
};

const AddToCartButton = (props: AddToCartButtonProps) => {
    const {
        material,
        materialIndex,
        t,
        title = '',
        className = '',
        size,
        text = t('common.materialBlock.addToCart'),
        alreadyInCartText = t('common.materialBlock.alreadyInCart'),
        type = 'button',
        cartIconName = 'cart',
        onAddToCartCallback,
        qualityEventsExtra,
        onAddToCartSuccess,
        onAddToCartError,
        isDisabled,
        shouldPreventCartOpen,
        addToCartData,
        scrollToTopOnSuccess,
        hideRestrictionsMessage,
        areAllMaterialsInCart
    } = props;

    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const favorite = useSelector((state: RootState) => state.main.favorite);
    const cart = useSelector((state: RootState) => state.main.cart);
    const user = useSelector((state: RootState) => state.main.user);
    const dispatch = useDispatch();
    const { changeFavoriteItem } = useChangeFavorite();
    const materialAlreadyInCart = areAllMaterialsInCart || isMaterialInCart(material.id, cart);

    const removeItem = () => {
        removeFromCart({}, { id: material.id }).then(
            ({
                data: {
                    items: { cart }
                }
            }) => {
                resetCart(cart);
            }
        );

        enhancedEcommerce.tracker.removeFromCart(material);
        qualityEvents.cartPage.removeFromCart({
            item: material,
            itemIndex: materialIndex,
            source: CART_REMOVE_FROM_CART_SOURCE.MATERIAL_CARD
        });
    };

    const resetCart = (cart: Cart) => {
        dispatch(reset(cart));
    };

    const addToCart = () => {
        if (isAddingToCart || materialAlreadyInCart) {
            return;
        }

        const materialItem = window.sessionStorage.getItem(`material_${material.id}`);

        aiStripeEvents(materialItem, material.id.toString(), 'AddToCart');

        setIsAddingToCart(true);
        enhancedEcommerce.tracker.addToCart([{ ...material }]);

        const isFavoritesPage = window.location.pathname.indexOf(Routes.FAVORITES.path) >= 0;
        const from = isFavoritesPage ? 'fromFavorites' : undefined;

        qualityEvents.materialPage.priceBlock.addToCart(
            material,
            materialItem,
            materialIndex,
            from,
            qualityEventsExtra
        );
        onAddToCartCallback && onAddToCartCallback(material);

        const cartData = addToCartData ?? {
            material: {
                id: material.id,
                purchaseType: 1
            }
        };

        addToCartApi(cartData)
            .then((response) => {
                const { cart } = response.data.items;
                const inFavorites = isInFavorites(material.id, favorite);

                if (material.interactiveId && !session.isLoggedIn()) {
                    pubsub.publish('showAddToCartModal', { type: ADD_TO_CART_MODAL_TYPES.INTERACTIVE_SUCCESS });
                } else if (!shouldPreventCartOpen) {
                    pubsub.publish('cartSlideDown', {});
                    scrollToTopOnSuccess &&
                        setTimeout(() => {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }, 200);
                }

                if (inFavorites) {
                    changeFavoriteItem({ item: material, add: false, itemIndex: materialIndex, isApproved: true });
                }

                const idsOfMaterialsIncludedInBundle = material.bundleMaterials?.map((material) => material.id) ?? [];

                onAddToCartSuccess ? onAddToCartSuccess({ cart, idsOfMaterialsIncludedInBundle }) : resetCart(cart);
                setIsAddingToCart(false);
            })
            .catch(() => {
                setIsAddingToCart(false);
                onAddToCartError
                    ? onAddToCartError()
                    : pubsub.publish('showAddToCartModal', { type: ADD_TO_CART_MODAL_TYPES.ERROR });
            });
    };

    const goToCartWhenDisabled = () => {
        qualityEvents.materialPage.priceBlock.addToCartInactive(qualityEventsExtra);
        window.location.href = `${Routes.WARENKORB.path}`;
    };

    const handleClick = async (): Promise<void> => {
        const shouldAllowClick = !onAddToCartCallback || (await onAddToCartCallback(material));

        if (!shouldAllowClick) {
            onAddToCartError?.();

            return;
        }

        if (materialAlreadyInCart) {
            removeItem();
        } else {
            addToCart();
        }
    };

    const isButtonDisabled = (schoolAccount.isAdmin(user) && material.hasFixedPrice) || isDisabled || isAddingToCart;

    return (
        <>
            {['button', 'disableable-button'].includes(type) && (
                <>
                    {!materialAlreadyInCart && (
                        <>
                            <Button
                                disabled={isButtonDisabled}
                                title={title}
                                variant={`add-to-cart-btn ${className}`}
                                size={size}
                                onClick={handleClick}
                            >
                                {isAddingToCart ? <Spinner size={25} /> : <>{text}</>}
                            </Button>
                            {isButtonDisabled && !hideRestrictionsMessage && (
                                <div className='mt-2 mb-2'>{t('common.materialBlock.restrictedMaterialForSchool')}</div>
                            )}
                        </>
                    )}
                    {materialAlreadyInCart && (
                        <Button
                            title={title}
                            disabled={type === 'disableable-button'}
                            variant={`add-to-cart-btn disabled ${className}`}
                            size={size}
                            onClick={goToCartWhenDisabled}
                        >
                            <>{alreadyInCartText}</>
                        </Button>
                    )}
                </>
            )}
            {type === 'icon' && (
                <>
                    {isAddingToCart ? (
                        <span className='cart-icon'>
                            <Spinner size={25} />
                        </span>
                    ) : (
                        <Icon
                            active={materialAlreadyInCart}
                            width={26}
                            name={cartIconName}
                            onClick={handleClick}
                            disabled={isButtonDisabled}
                        />
                    )}
                </>
            )}
            {type === 'icon-button' && (
                <>
                    {!materialAlreadyInCart && (
                        <>
                            <Button
                                disabled={isButtonDisabled}
                                title={title}
                                variant={`add-to-cart-btn ${className}`}
                                size={size}
                                onClick={handleClick}
                            >
                                <>
                                    {!isAddingToCart && (
                                        <Icon className='mr-3' active={materialAlreadyInCart} name={cartIconName} />
                                    )}
                                    {!isAddingToCart && <>{text}</>}
                                    {isAddingToCart && <Spinner size={25} />}
                                </>
                            </Button>
                            {isButtonDisabled && !hideRestrictionsMessage && (
                                <div className='mt-2 mb-2'>{t('common.materialBlock.restrictedMaterialForSchool')}</div>
                            )}
                        </>
                    )}
                    {materialAlreadyInCart && (
                        <Button
                            disabled={isButtonDisabled}
                            title={title}
                            variant={`add-to-cart-btn disabled ${className}`}
                            size={size}
                            onClick={goToCartWhenDisabled}
                        >
                            <>
                                {!isAddingToCart && (
                                    <Icon className='mr-3' active={materialAlreadyInCart} name={cartIconName} />
                                )}
                                {alreadyInCartText}
                            </>
                        </Button>
                    )}
                </>
            )}
        </>
    );
};

const translate = translation('common');

export default translate(AddToCartButton);
